import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Text, Flex, ScrollArea, Button, Modal, useMantineTheme, Skeleton, Title, Box, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSearch, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import LocationForm from '../forms/LocationForm';
import { notifications } from '@mantine/notifications';
import { getLocations, deleteLocation } from '../../services/api.service';
import { modals } from '@mantine/modals';
import DeleteButton from '../buttons/DeleteButton';
import dayjs from 'dayjs';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const Locations = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [locations, setLocations] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [locationEdit, setLocationEdit] = useState({});
	const [filteredData, setFilteredData] = useState([]);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);

	// On load
	useEffect(() => {
		getLocationsList();
		// eslint-disable-next-line
	}, []);

	// Get locations
	const getLocationsList = () => {

		// Get
		getLocations(function (response) {

			// Set locations and initial sorted data, remove loader
			setLocations(response.data);
			setFilteredData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				Nombre
			</Th>
			<Th>
				Dirección
			</Th>
			<Th>
				Latitud
			</Th>
			<Th>
				Longitud
			</Th>
			<Th>
				Inicio
			</Th>
			<Th>
				Fin
			</Th>
			<Th></Th>
		</tr>
	);

	// Build rows
	const rows = filteredData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
			onClick={(event) => toggleLocationEdit(event, element)}
		>
			<td>
				{element.name}
			</td>
			<td>
				{element.address}
			</td>
			<td>
				{element.latlng?.coordinates?.[0]}
			</td>
			<td>
				{element.latlng?.coordinates?.[1]}
			</td>
			<td>
				{element.startDate ? dayjs(new Date(element.startDate)).format('DD-MM-YYYY HH:mm') : 'N/A'}
			</td>
			<td>
				{element.endDate ? dayjs(new Date(element.endDate)).format('DD-MM-YYYY HH:mm') : 'N/A'}
			</td>
			<td>
				<DeleteButton
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		filterData(locations, value);
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase().includes(query))));
	}

	// Close locations modal
	const closeModal = () => {
		// Get locations and close modal
		getLocationsList();
		setIsEdit(false);
		setLocationEdit({});
		close();
	}

	// Toggle location edit
	const toggleLocationEdit = (event, location) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setLocationEdit(location);
		// Open modal
		open();
	}

	// Open delete confirmation modal
	const openDeleteModal = (id) => 
		modals.openConfirmModal({
			title: 'Eliminar Ubicación',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar esta ubicación? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar ubicación', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => {}, // Nothing
			onConfirm: () => triggerDeleteLocation(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete location
	const triggerDeleteLocation = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deleteLocation(id, function () {

			// Show success
			notifications.show({
				title: 'Ubicación eliminada correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
				withinPortal
			>
				{/* New Location Form */}
				<LocationForm
					closeModal={closeModal}
					isEdit={isEdit}
					location={locationEdit}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Box className="mainContainer">
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
					className={classes.container}
				>
					{/* Title */}
					<Title mb="xl">
						Ubicaciones
					</Title>
					{/* Group */}
					<Flex direction="column">
						{/* Top */}
						<Flex mb="md">
							{/* New Location */}
							<Button
								onClick={open}
								variant="filled"
							>
								Nueva Ubicación
							</Button>
							{/* Search */}
							<TextInput
								placeholder="Buscar ubicaciones"
								icon={<IconSearch size="0.9rem" stroke={1.5} />}
								value={search}
								onChange={handleSearchChange}
								ml="sm"
								sx={{
									width: '100%',
									maxWidth: 350
								}}
								variant="filled"
							/>
						</Flex>
						{/* Table */}
						<ScrollArea>
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={7}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
				</Flex>
			</Box>
		</>
	);

}

export default Locations;
